/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    code: "code",
    h3: "h3",
    a: "a",
    pre: "pre",
    blockquote: "blockquote",
    h4: "h4",
    em: "em"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "服务器端渲染(Server Side Rendering)"
    }), "\n", _jsx(_components.p, {
      children: "¥Server Side Rendering"
    }), "\n", _jsx(_components.p, {
      children: "| v2+"
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components 支持并发服务器端渲染，并具有样式表补水功能。基本思想是，每次在服务器上渲染应用时，你都可以创建一个 ", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " 并将提供程序添加到你的 React 树中，该提供程序通过上下文 API 接受样式。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥styled-components supports concurrent server side rendering, with stylesheet rehydration.\nThe basic idea is that everytime you render your app on the server, you can create\na ", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " and add a provider to your React tree, that accepts styles\nvia a context API."]
    }), "\n", _jsxs(_components.p, {
      children: ["这不会干扰全局样式，例如 ", _jsx(_components.code, {
        children: "keyframes"
      }), " 或 ", _jsx(_components.code, {
        children: "createGlobalStyle"
      }), "，并允许你将样式组件与 React DOM 的各种 SSR API 一起使用。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥This doesn't interfere with global styles, such as ", _jsx(_components.code, {
        children: "keyframes"
      }), " or ", _jsx(_components.code, {
        children: "createGlobalStyle"
      }), " and\nallows you to use styled-components with React DOM's various SSR APIs."]
    }), "\n", _jsx(_components.h3, {
      children: "工具设置(Tooling setup)"
    }), "\n", _jsx(_components.p, {
      children: "¥Tooling setup"
    }), "\n", _jsxs(_components.p, {
      children: ["为了可靠地执行服务器端渲染并让客户端包顺利获取，你需要使用我们的 ", _jsx(_components.a, {
        href: "/docs/tooling#babel-plugin",
        children: "babel 插件"
      }), "。它通过向每个样式组件添加确定性 ID 来防止校验和不匹配。请参阅 ", _jsx(_components.a, {
        href: "/docs/tooling#serverside-rendering",
        children: "工具文档"
      }), " 了解更多信息。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥In order to reliably perform server side rendering and have the client side bundle pick up without issues, you'll need to use our ", _jsx(_components.a, {
        href: "/docs/tooling#babel-plugin",
        children: "babel plugin"
      }), ". It prevents checksum mismatches by adding a deterministic ID to each styled component. Refer to the ", _jsx(_components.a, {
        href: "/docs/tooling#serverside-rendering",
        children: "tooling documentation"
      }), " for more information."]
    }), "\n", _jsxs(_components.p, {
      children: ["对于 TypeScript 用户，我们的常驻 TS 大师 Igor Oleinikov 为 webpack ts-loader / Awesome-typescript-loader 工具链整理了一个 ", _jsx(_components.a, {
        href: "/docs/tooling#typescript-plugin",
        children: "TypeScript 插件"
      }), "，可以完成一些类似的任务。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥For TypeScript users, our resident TS guru Igor Oleinikov put together a ", _jsx(_components.a, {
        href: "/docs/tooling#typescript-plugin",
        children: "TypeScript plugin"
      }), " for the webpack ts-loader / awesome-typescript-loader toolchain that accomplishes some similar tasks."]
    }), "\n", _jsxs(_components.p, {
      children: ["如果可能的话，我们绝对推荐使用 babel 插件，因为它更新最频繁。现在可以实现 ", _jsx(_components.a, {
        href: "https://babel.nodejs.cn/docs/babel-preset-typescript",
        children: "使用 Babel 编译 TypeScript"
      }), "，因此可能值得关闭 TS 加载器并使用纯 Babel 实现来获得生态系统的好处。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥If possible, we definitely recommend using the babel plugin though because it is updated the most frequently. It's now possible to ", _jsx(_components.a, {
        href: "https://babel.nodejs.cn/docs/babel-preset-typescript",
        children: "compile TypeScript using Babel"
      }), ", so it may be worth switching off TS loader and onto a pure Babel implementation to reap the ecosystem benefits."]
    }), "\n", _jsx(_components.h3, {
      children: "示例(Example)"
    }), "\n", _jsx(_components.p, {
      children: "¥Example"
    }), "\n", _jsx(_components.p, {
      children: "基本的 API 如下："
    }), "\n", _jsx(_components.p, {
      children: "¥The basic API goes as follows:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToString } from 'react-dom/server';\nimport { ServerStyleSheet } from 'styled-components';\n\nconst sheet = new ServerStyleSheet();\ntry {\n  const html = renderToString(sheet.collectStyles(<YourApp />));\n  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();\n} catch (error) {\n  // handle error\n  console.error(error);\n} finally {\n  sheet.seal();\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "collectStyles"
      }), " 方法将你的元素封装在提供程序中。你也可以选择直接使用 ", _jsx(_components.code, {
        children: "StyleSheetManager"
      }), " 提供程序，而不是使用此方法。只要确保不要在客户端使用它即可。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥The ", _jsx(_components.code, {
        children: "collectStyles"
      }), " method wraps your element in a provider. Optionally you can use\nthe ", _jsx(_components.code, {
        children: "StyleSheetManager"
      }), " provider directly, instead of this method. Just make sure not to\nuse it on the client-side."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToString } from 'react-dom/server';\nimport { ServerStyleSheet, StyleSheetManager } from 'styled-components';\n\nconst sheet = new ServerStyleSheet();\ntry {\n  const html = renderToString(\n    <StyleSheetManager sheet={sheet.instance}>\n      <YourApp />\n    </StyleSheetManager>\n  );\n  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();\n} catch (error) {\n  // handle error\n  console.error(error);\n} finally {\n  sheet.seal();\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " 返回多个 ", _jsx(_components.code, {
        children: "<style>"
      }), " 标签的字符串。将 CSS 字符串添加到 HTML 输出时需要考虑到这一点。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥The ", _jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " returns a string of multiple ", _jsx(_components.code, {
        children: "<style>"
      }), " tags.\nYou need to take this into account when adding the CSS string to your HTML output."]
    }), "\n", _jsxs(_components.p, {
      children: ["另外，", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " 实例还有一个 ", _jsx(_components.code, {
        children: "getStyleElement()"
      }), " 方法，该方法返回 React 元素数组。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Alternatively the ", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " instance also has a ", _jsx(_components.code, {
        children: "getStyleElement()"
      }), " method\nthat returns an array of React elements."]
    }), "\n", _jsxs(_components.p, {
      children: ["如果由于任何原因渲染失败，最好使用 ", _jsx(_components.code, {
        children: "try...catch...finally"
      }), " 来确保 ", _jsx(_components.code, {
        children: "sheet"
      }), " 对象始终可用于垃圾回收。确保仅在调用 ", _jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " 或 ", _jsx(_components.code, {
        children: "sheet.getStyleElement()"
      }), " 之后调用 ", _jsx(_components.code, {
        children: "sheet.seal()"
      }), "，否则将引发不同的错误。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥If rendering fails for any reason it's a good idea to use ", _jsx(_components.code, {
        children: "try...catch...finally"
      }), " to ensure that the ", _jsx(_components.code, {
        children: "sheet"
      }), " object will always be available for garbage collection. Make sure ", _jsx(_components.code, {
        children: "sheet.seal()"
      }), " is only called after ", _jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " or ", _jsx(_components.code, {
        children: "sheet.getStyleElement()"
      }), " have been called otherwise a different error will be thrown."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "sheet.getStyleTags()"
        }), " 和 ", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " 只能在元素渲染后调用。因此，", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " 中的组件无法与 ", _jsx(_components.code, {
          children: "<YourApp />"
        }), " 组合成更大的组件。"]
      }), "\n", _jsxs(_components.p, {
        children: ["¥", _jsx(_components.code, {
          children: "sheet.getStyleTags()"
        }), " and ", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " can only be called after your element is rendered. As a result, components from ", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " cannot be combined with ", _jsx(_components.code, {
          children: "<YourApp />"
        }), " into a larger component."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Next.js(Next.js)"
    }), "\n", _jsx(_components.h4, {
      children: "使用 Babel(With Babel)"
    }), "\n", _jsx(_components.p, {
      children: "¥With Babel"
    }), "\n", _jsxs(_components.p, {
      children: ["基本上你需要添加一个自定义 ", _jsx(_components.code, {
        children: "pages/_document.js"
      }), "（如果你没有）。然后 ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components-babel/pages/_document.tsx",
        children: "复制逻辑"
      }), " 为样式组件将服务器端渲染的样式注入到 ", _jsx(_components.code, {
        children: "<head>"
      }), " 中。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Basically you need to add a custom ", _jsx(_components.code, {
        children: "pages/_document.js"
      }), " (if you don't have one). Then ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components-babel/pages/_document.tsx",
        children: "copy the logic"
      }), " for styled-components to inject the server side rendered styles into the ", _jsx(_components.code, {
        children: "<head>"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["请参阅 Next.js 存储库中的 ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components-babel",
        children: "我们的例子"
      }), " 以获取最新的使用示例。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Refer to ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components-babel",
        children: "our example"
      }), " in the Next.js repo for an up-to-date usage example."]
    }), "\n", _jsx(_components.h4, {
      children: "使用 SWC(With SWC)"
    }), "\n", _jsx(_components.p, {
      children: "¥With SWC"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://next.nodejs.cn/blog/next-12",
        children: "从版本 12 开始"
      }), "，Next.js 使用名为 SWC 的 Rust 编译器。如果你没有使用任何 babel 插件，你应该参考 ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components",
        children: "这个例子"
      }), "。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.a, {
        href: "https://next.nodejs.cn/blog/next-12",
        children: "Since version 12"
      }), ", Next.js uses a Rust compiler called SWC. If you're not using any babel plugin, you should refer to ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components",
        children: "this example"
      }), " instead."]
    }), "\n", _jsxs(_components.p, {
      children: ["在此版本中，你可以在 ", _jsx(_components.code, {
        children: "next.config.js"
      }), " 文件中的编译器选项中进行 ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/next.config.js",
        children: "只需要添加"
      }), " ", _jsx(_components.code, {
        children: "styledComponents: true,"
      }), " 并使用 ", _jsx(_components.code, {
        children: "getInitialProps"
      }), " 修改 ", _jsx(_components.code, {
        children: "_document"
      }), " 文件（如本 ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/pages/_document.tsx",
        children: "example"
      }), " 中所示）以支持 SSR。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥On this version, you ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/next.config.js",
        children: "only need to add"
      }), " ", _jsx(_components.code, {
        children: "styledComponents: true,"
      }), " at the compiler options in the ", _jsx(_components.code, {
        children: "next.config.js"
      }), " file and modify ", _jsx(_components.code, {
        children: "_document"
      }), " file with ", _jsx(_components.code, {
        children: "getInitialProps"
      }), " as in this ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/pages/_document.tsx",
        children: "example"
      }), " to support SSR."]
    }), "\n", _jsx(_components.h4, {
      children: "应用目录(App directory)"
    }), "\n", _jsx(_components.p, {
      children: "¥App directory"
    }), "\n", _jsxs(_components.p, {
      children: ["对于在 ", _jsx(_components.code, {
        children: "app/"
      }), " 目录中定义的路由，在 Next.js v13+ 中，你需要将样式组件注册表放入布局文件之一中，如 ", _jsx(_components.a, {
        href: "https://next.nodejs.cn/docs/app/building-your-application/styling/css-in-js#styled-components",
        children: "Next.js 文档中描述"
      }), "。请注意，这取决于 styled-components v6+。另请注意，使用了 ", _jsx(_components.code, {
        children: "'use client'"
      }), " 指令 - 因此，虽然你的页面将在服务器端渲染，但样式组件仍将出现在你的客户端包中。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥For routes defined in the ", _jsx(_components.code, {
        children: "app/"
      }), " directory, in Next.js v13+, you'll need to put a styled-components registry in one of your layout files, as ", _jsx(_components.a, {
        href: "https://next.nodejs.cn/docs/app/building-your-application/styling/css-in-js#styled-components",
        children: "described in Next.js docs"
      }), ". Note that this depends on styled-components v6+. Also note that the ", _jsx(_components.code, {
        children: "'use client'"
      }), " directive is used - so while your page will be server-side rendered, styled-components will still appear in your client bundle."]
    }), "\n", _jsx(_components.h3, {
      children: "Gatsby(Gatsby)"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.gatsbyjs.org/",
        children: "Gatsby"
      }), " 有一个官方插件，可以为样式组件启用服务器端渲染。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.a, {
        href: "https://www.gatsbyjs.org/",
        children: "Gatsby"
      }), " has an official plugin that enables server-side rendering for styled-components."]
    }), "\n", _jsxs(_components.p, {
      children: ["请参阅 ", _jsx(_components.a, {
        href: "https://www.gatsbyjs.org/packages/gatsby-plugin-styled-components/",
        children: "插件页面"
      }), " 了解设置和使用说明。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Refer to ", _jsx(_components.a, {
        href: "https://www.gatsbyjs.org/packages/gatsby-plugin-styled-components/",
        children: "the plugin's page"
      }), " for setup and usage instructions."]
    }), "\n", _jsx(_components.h3, {
      children: "流式渲染(Streaming Rendering)"
    }), "\n", _jsx(_components.p, {
      children: "¥Streaming Rendering"
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components 提供了一个与 ", _jsx(_components.a, {
        href: "https://react.nodejs.cn/docs/react-dom-server.html#rendertonodestream",
        children: "ReactDOMServer.renderToNodeStream()"
      }), " 一起使用的流 API。流实现有两个部分："]
    }), "\n", _jsxs(_components.p, {
      children: ["¥styled-components offers a streaming API for use with ", _jsx(_components.a, {
        href: "https://react.nodejs.cn/docs/react-dom-server.html#rendertonodestream",
        children: "ReactDOMServer.renderToNodeStream()"
      }), ". There are two parts to a streaming implementation:"]
    }), "\n", _jsx(_components.p, {
      children: "在服务器上："
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.em, {
        children: "On the server:"
      })]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "ReactDOMServer.renderToNodeStream"
      }), " 触发一个由 styled-components 封装的 \"readable\" 流。当整个 HTML 块被推送到流上时，如果任何相应的样式准备好渲染，样式块就会被添加到 React 的 HTML 中并转发到客户端浏览器。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.code, {
        children: "ReactDOMServer.renderToNodeStream"
      }), " emits a \"readable\" stream that styled-components wraps. As whole chunks of HTML are pushed onto the stream, if any corresponding styles are ready to be rendered, a style block is prepended to React's HTML and forwarded on to the client browser."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToNodeStream } from 'react-dom/server';\nimport styled, { ServerStyleSheet } from 'styled-components';\n\n// if you're using express.js, you'd have access to the response object \"res\"\n\n// typically you'd want to write some preliminary HTML, since React doesn't handle this\nres.write('<html><head><title>Test</title></head><body>');\n\nconst Heading = styled.h1`\n  color: red;\n`;\n\nconst sheet = new ServerStyleSheet();\nconst jsx = sheet.collectStyles(<Heading>Hello SSR!</Heading>);\nconst stream = sheet.interleaveWithNodeStream(renderToNodeStream(jsx));\n\n// you'd then pipe the stream into the response object until it's done\nstream.pipe(res, { end: false });\n\n// and finalize the response with closing HTML\nstream.on('end', () => res.end('</body></html>'));\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "在客户端："
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.em, {
        children: "On the client:"
      })]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { hydrate } from 'react-dom';\n\nhydrate();\n// your client-side react implementation\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "客户端补水完成后，样式组件将照常接管，并在重新定位的流式样式之后注入任何进一步的动态样式。"
    }), "\n", _jsx(_components.p, {
      children: "¥After client-side rehydration is complete, styled-components will take over as usual and inject any further dynamic styles after the relocated streaming ones."
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
